/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { useObservable } from '@squiz/dxp-console-support';
import { EntityDxpUserMeOrganisation } from 'apps/console-core/src/Services/console';
import { useConsoleServiceStore } from 'dxpConsoleCore-mf/State';
import { useMemo } from 'react';
import { Observable } from 'rxjs';
import { JsonObject } from 'type-fest';
import { EntityEventSubscription } from '../../Entities';
import { EventBusGetEventSubscriptionsDto } from '../../EventBus.service.dto';
import { getEventBusService } from '../../Utilities/GetEventBusService/GetEventBusService';

/**
 * The result expected of the useEventSubscriptions hook.
 * @export
 */
export type EventSubscriptionsResult = [Array<EntityEventSubscription> | null, JsonObject | null];

/**
 * React hook to get a list of event subscriptions.
 * @export
 * @returns {EventSubscriptionsResult} The event subscriptions result.
 */
export function useEventSubscriptions(
  params: Partial<Omit<EventBusGetEventSubscriptionsDto, 'tenantId'>> = {},
): EventSubscriptionsResult {
  const eventBusService = getEventBusService();

  const { tenant }: { tenant: EntityDxpUserMeOrganisation } = useConsoleServiceStore();
  const { tenantReferenceId } = tenant;

  const eventSubscriptions$ = useMemo((): Observable<Array<EntityEventSubscription> | null> => {
    const dto: EventBusGetEventSubscriptionsDto = {
      tenantId: tenantReferenceId,
      ...params,
    };

    return eventBusService.getEventSubscriptions(dto);
  }, [eventBusService, params, tenantReferenceId]);

  const [eventSubscriptions, error] = useObservable(eventSubscriptions$, [eventBusService]);

  return useMemo((): EventSubscriptionsResult => {
    return [eventSubscriptions as Array<EntityEventSubscription>, error as JsonObject];
  }, [error, eventSubscriptions]);
}
